import React, {useEffect, useRef} from 'react';
import {Box, Paper, Typography, Stack} from '@mui/material';
import {AnyQuestion, Form} from "crqlar-react-common/dist/models/forms/form";
import FormThemeProvider from "../FormThemeProvider";
import QuestionInput from "./questionInputs/QuestionInput";
import {useTranslation} from "react-i18next";

interface SimpleFormEditorProps {
    form: Form;
    currentQuestionId: string;
    onQuestionSelect: (questionId: string) => void;
    onQuestionUpdate: (question: AnyQuestion) => void;
}

const SimpleFormEditor: React.FC<SimpleFormEditorProps> = ({
                                                               form,
                                                               currentQuestionId,
                                                       onQuestionSelect,
                                                       onQuestionUpdate
                                                   }) => {
    const {t} = useTranslation();
    const questions = form.questions || [];
    const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Scroll to the selected question when currentQuestionId changes
    useEffect(() => {
        if (currentQuestionId && questionRefs.current[currentQuestionId]) {
            const element = questionRefs.current[currentQuestionId];
            element?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [currentQuestionId]);

    return (
        <FormThemeProvider theme={form.theme}>
            <Box
                sx={{
                    height: '100%',
                    overflowY: 'auto',
                    p: 4
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        maxWidth: 800,
                        mx: 'auto',
                        p: 4,
                        backgroundColor: 'background.paper',
                        borderRadius: 2
                    }}
                >
                    {/* Questions List */}
                    {questions.length === 0 ? (
                        <Box
                            sx={{
                                py: 8,
                                textAlign: 'center',
                                color: 'text.secondary'
                            }}
                        >
                            <Typography variant="body1">
                                {t('No questions yet. Add questions using the sidebar.')}
                            </Typography>
                        </Box>
                    ) : (
                        <Stack gap={
                            form.theme?.shape?.questionSpacing ? `${form.theme?.shape?.questionSpacing}px` : 2
                        }>
                            {questions.map((question) => (
                                <Box
                                    key={question.id}
                                    ref={(el: HTMLDivElement | null) => {
                                        questionRefs.current[question.id] = el;
                                    }}
                                >
                                    <QuestionInput
                                        question={question}
                                        onQuestionUpdate={onQuestionUpdate}
                                        isSelected={question.id === currentQuestionId}
                                        onSelect={() => onQuestionSelect(question.id)}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Paper>
            </Box>
        </FormThemeProvider>
    );
};

export default SimpleFormEditor;
